// src/components/resources/tools/generators/FakeChat/accentThemes.ts

/**
 * Theme configuration for different accent colors
 * @interface AccentTheme
 * @property {string} text - The text color.
 * @property {string} badge - The badge color.
 * @property {string} bubble - The bubble color.
 * @property {string} neutralBubble - The neutral bubble color.
 * @property {string} typingDot - The typing dot color.
 * @property {string} slackBadge - The Slack badge color.
 * @property {string} slackReaction - The Slack reaction color.
 * @property {string} slackThread - The Slack thread color.
 * @property {string} slackComposer - Accent applied to the Slack composer send button.
 * @property {string} imessageOutgoing - Text styling for outgoing iMessage bubbles.
 * @property {string} imessageIncoming - Text styling for incoming iMessage bubbles.
 * @property {string} imessageSendButton - Styling for the iMessage composer send button.
 * @property {string} imessageOutgoingHex - Hex color for outgoing iMessage bubbles.
 * @property {string} imessageIncomingHex - Hex color for incoming iMessage bubbles.
 */
export type AccentTheme = {
  text: string
  badge: string
  bubble: string
  neutralBubble: string
  typingDot: string
  slackBadge: string
  slackReaction: string
  slackThread: string
  slackComposer: string
  imessageOutgoing: string
  imessageIncoming: string
  imessageSendButton: string
  imessageOutgoingHex: string
  imessageIncomingHex: string
}

const accentThemes: Record<string, AccentTheme> = {
  'indigo-500': {
    text: 'text-indigo-500',
    badge: 'bg-indigo-500/10 text-indigo-600',
    bubble: 'bg-indigo-500 text-white',
    neutralBubble: 'bg-slate-100 text-slate-900',
    typingDot: 'bg-indigo-400',
    slackBadge: 'border border-[#d7d2f4] bg-[#f2efff] text-[#3f27a0]',
    slackReaction: 'border border-[#d7d2f4] bg-[#f2efff] text-[#3f27a0]',
    slackThread: 'text-[#1164a3]',
    slackComposer: 'bg-[#3f27a0] text-white',
    imessageOutgoing: 'text-white',
    imessageIncoming: 'text-[#050505]',
    imessageSendButton: 'bg-[#007aff]',
    imessageOutgoingHex: '#0b93f6',
    imessageIncomingHex: '#e5e5ea'
  },
  'sky-500': {
    text: 'text-sky-600',
    badge: 'bg-sky-100 text-sky-700',
    bubble: 'bg-sky-500 text-white',
    neutralBubble: 'bg-slate-50 text-slate-900',
    typingDot: 'bg-sky-400',
    slackBadge: 'border border-[#bae6fd] bg-[#e0f2fe] text-[#0f6fae]',
    slackReaction: 'border border-[#bae6fd] bg-[#e0f2fe] text-[#0f6fae]',
    slackThread: 'text-[#0f6fae]',
    slackComposer: 'bg-[#0f6fae] text-white',
    imessageOutgoing: 'text-white',
    imessageIncoming: 'text-[#050505]',
    imessageSendButton: 'bg-[#007aff]',
    imessageOutgoingHex: '#0b93f6',
    imessageIncomingHex: '#f1f1f4'
  },
  'emerald-500': {
    text: 'text-emerald-600',
    badge: 'bg-emerald-100 text-emerald-700',
    bubble: 'bg-emerald-500 text-white',
    neutralBubble: 'bg-slate-50 text-slate-900',
    typingDot: 'bg-emerald-400',
    slackBadge: 'border border-[#bbf7d0] bg-[#dcfce7] text-[#0f8a5f]',
    slackReaction: 'border border-[#bbf7d0] bg-[#dcfce7] text-[#0f8a5f]',
    slackThread: 'text-[#0f8a5f]',
    slackComposer: 'bg-[#0f8a5f] text-white',
    imessageOutgoing: 'text-white',
    imessageIncoming: 'text-[#050505]',
    imessageSendButton: 'bg-[#007aff]',
    imessageOutgoingHex: '#0b93f6',
    imessageIncomingHex: '#f1f5f9'
  },
  'rose-500': {
    text: 'text-rose-600',
    badge: 'bg-rose-100 text-rose-700',
    bubble: 'bg-rose-500 text-white',
    neutralBubble: 'bg-slate-50 text-slate-900',
    typingDot: 'bg-rose-400',
    slackBadge: 'border border-[#fecdd3] bg-[#ffe4e6] text-[#be123c]',
    slackReaction: 'border border-[#fecdd3] bg-[#ffe4e6] text-[#be123c]',
    slackThread: 'text-[#be123c]',
    slackComposer: 'bg-[#be123c] text-white',
    imessageOutgoing: 'text-white',
    imessageIncoming: 'text-[#050505]',
    imessageSendButton: 'bg-[#007aff]',
    imessageOutgoingHex: '#0b93f6',
    imessageIncomingHex: '#f8fafc'
  },
  'amber-500': {
    text: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-700',
    bubble: 'bg-amber-500 text-slate-900',
    neutralBubble: 'bg-slate-50 text-slate-900',
    typingDot: 'bg-amber-400',
    slackBadge: 'border border-[#fde68a] bg-[#fef3c7] text-[#b45309]',
    slackReaction: 'border border-[#fde68a] bg-[#fef3c7] text-[#b45309]',
    slackThread: 'text-[#b45309]',
    slackComposer: 'bg-[#b45309] text-white',
    imessageOutgoing: 'text-white',
    imessageIncoming: 'text-[#050505]',
    imessageSendButton: 'bg-[#007aff]',
    imessageOutgoingHex: '#0b93f6',
    imessageIncomingHex: '#fef3c7'
  },
  'slate-500': {
    text: 'text-slate-600',
    badge: 'bg-slate-100 text-slate-700',
    bubble: 'bg-slate-600 text-white',
    neutralBubble: 'bg-white text-slate-900',
    typingDot: 'bg-slate-400',
    slackBadge: 'border border-[#d9dde3] bg-[#eef1f5] text-[#27303f]',
    slackReaction: 'border border-[#d9dde3] bg-[#eef1f5] text-[#27303f]',
    slackThread: 'text-[#1264a3]',
    slackComposer: 'bg-[#27303f] text-white',
    imessageOutgoing: 'text-white',
    imessageIncoming: 'text-[#050505]',
    imessageSendButton: 'bg-[#007aff]',
    imessageOutgoingHex: '#0b93f6',
    imessageIncomingHex: '#f1f5f9'
  }
}


/**
 * Default theme fallback
 */
const DEFAULT_THEME: AccentTheme = accentThemes['indigo-500'] ?? {
  text: 'text-indigo-500',
  badge: 'bg-indigo-500/10 text-indigo-600',
  bubble: 'bg-indigo-500 text-white',
  neutralBubble: 'bg-slate-800 text-slate-100',
  typingDot: 'bg-indigo-400',
  slackBadge: 'border border-[#d7d2f4] bg-[#f2efff] text-[#3f27a0]',
  slackReaction: 'border border-[#d7d2f4] bg-[#f2efff] text-[#3f27a0]',
  slackThread: 'text-[#1164a3]',
  slackComposer: 'bg-[#3f27a0] text-white',
  imessageOutgoing: 'text-white',
  imessageIncoming: 'text-[#050505]',
  imessageSendButton: 'bg-[#007aff]',
  imessageOutgoingHex: '#0b93f6',
  imessageIncomingHex: '#e5e5ea'
}

/**
 * Get theme with fallback to default
 * @param {string} accentColor - The accent color to get the theme for.
 * @returns {AccentTheme} The theme for the accent color.
 */
export function getTheme(accentColor: string): AccentTheme {
  return accentThemes[accentColor] ?? DEFAULT_THEME
}
